import type { RouterContextType } from "@/api/permissions/permissions.api";
import type { StepFormStepId } from "@/api/sp-invite/spInvite.endpoints";
import StepperFormLayout from "@/components/features/StepperFormLayout/StepperFormLayout";
import Loader from "@/components/ui/Loader/Loader";
import { useSpInviteForm, useSpInviteJsonForm } from "@/hooks/sp-invite/useSpInviteQueries";
import { generateDefaultValues } from "@/lib/generateDefaultValues";
import { buildZodSchemaFromJson } from "@/lib/jsonToZodSchema";
import { useSpInviteSessionStore } from "@/store/useSpInviteSessionStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, MantineProvider } from "@mantine/core";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";

export const Route = createFileRoute("/sp-invite/review/_reviewLayout")({
  component: ReviewLayout,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function ReviewLayout() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { stepId: StepFormStepId };
  const stepId = params.stepId as StepFormStepId;
  const location = useLocation();

  const serviceProviderId = useSpInviteSessionStore((s) => s.splashData?.serviceProviderId ?? null);

  const pathMatch = location.pathname.match(/\/sp-invite\/review\/([^/]+)\/([^/]+)/);
  const providerType = pathMatch && !["summary"].includes(pathMatch[1]) ? pathMatch[1] : null;
  const isCompleteSubmissionRoute = location.pathname.includes("/complete-submission");

  const {
    data: serviceProviderForm,
    isLoading,
    error,
  } = useSpInviteForm(providerType || undefined);

  const {
    data: serviceProviderFormStepData,
    isLoading: isStepLoading,
    error: isStepError,
  } = useSpInviteJsonForm(stepId as StepFormStepId, serviceProviderId ?? null);

  const firstStep = serviceProviderForm?.steps?.find((step) => step.stepNumber === 1);

  const isValidStepRoute = useMemo(() => {
    if (isCompleteSubmissionRoute) return true;
    if (!serviceProviderForm || !serviceProviderForm.steps) return false;

    const isBaseRoute =
      location.pathname === "/sp-invite/review" || location.pathname === "/sp-invite/review/";

    if (isBaseRoute) return false;

    const stepKeys = serviceProviderForm.steps.map((step) => step.stepKey);
    const isStepRoute = stepKeys.some((stepKey) => {
      if (providerType) {
        return (
          location.pathname === `/sp-invite/review/${providerType}/${stepKey}` ||
          location.pathname.includes(`/${providerType}/${stepKey}`)
        );
      }
      return false;
    });

    const isSummaryRoute = location.pathname.includes("/summary");

    return isStepRoute || isSummaryRoute;
  }, [isCompleteSubmissionRoute, serviceProviderForm, location.pathname, providerType]);

  useEffect(() => {
    if (firstStep && !isValidStepRoute) {
      if (providerType) {
        navigate({
          to: "/sp-invite/review/$providerType/$stepId",
          params: { providerType, stepId: firstStep.stepKey },
          replace: true,
        });
      } else {
        navigate({ to: "/sp-invite", replace: true });
      }
    }
  }, [firstStep, navigate, providerType, isValidStepRoute]);

  const { schema, defaultValues } = useMemo(() => {
    if (!serviceProviderForm || !serviceProviderForm.steps) {
      return { allFields: [], schema: null, defaultValues: {} };
    }

    const currentStep = serviceProviderForm.steps.find((step) => step.stepKey === stepId);

    const schemaFields = currentStep
      ? currentStep.sections?.flatMap(
          (section) => section.subSections?.flatMap((ss) => ss.fields) || [],
        ) || []
      : [];

    const apiFields = (
      serviceProviderFormStepData?.sections?.flatMap(
        (section: any) => section.subSections?.flatMap((ss: any) => ss.fields) || [],
      ) || []
    ).map((f: any) => ({ ...f, _fromApi: true }));

    const fields = [...schemaFields, ...apiFields];

    const mergedSchemaStructure = currentStep
      ? {
          steps: [
            {
              ...currentStep,
              sections:
                currentStep.sections?.map((section) => {
                  const apiSection = serviceProviderFormStepData?.sections?.find(
                    (s: any) => s.key === section.key,
                  );

                  return {
                    ...section,
                    subSections: [
                      ...(section.subSections || []),
                      ...(apiSection?.subSections || []),
                    ],
                  };
                }) || [],
            },
          ],
        }
      : { steps: [] };

    const zodSchema = buildZodSchemaFromJson(fields, mergedSchemaStructure);
    const defaults = generateDefaultValues(fields, mergedSchemaStructure);

    return {
      allFields: fields,
      schema: zodSchema,
      defaultValues: defaults,
    };
  }, [serviceProviderForm, serviceProviderFormStepData, stepId]);

  const methods = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      const currentValues = methods.getValues();
      const mergedValues = { ...defaultValues };

      const currentStep = serviceProviderForm?.steps?.find((step) => step.stepKey === stepId);
      const fields =
        currentStep?.sections?.flatMap(
          (section) => section.subSections?.flatMap((ss) => ss.fields) || [],
        ) || [];

      Object.keys(currentValues).forEach((key) => {
        const field = fields.find((f: any) => f.key === key);

        if (field?.formControlType === "multiple_inputs") {
          const shouldHaveInitialItem = field.isRequired || (field.minItems && field.minItems > 0);

          const currentValue = currentValues[key];
          const defaultValue = defaultValues[key];

          if (
            shouldHaveInitialItem &&
            Array.isArray(currentValue) &&
            currentValue.length > 0 &&
            (!Array.isArray(defaultValue) || defaultValue.length === 0)
          ) {
            mergedValues[key] = currentValue;
          }
        } else if (
          !(key in defaultValues) &&
          currentValues[key] !== undefined &&
          currentValues[key] !== null
        ) {
          mergedValues[key] = currentValues[key];
        }
      });

      methods.reset(mergedValues);
    }
  }, [defaultValues, methods, serviceProviderForm, stepId]);

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === "hasMailingAddress" && !value.hasMailingAddress) {
        methods.clearErrors("mailingAddress");
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const reviewFormSteps = useMemo(() => {
    if (!serviceProviderForm || !providerType || !serviceProviderForm.steps) return [];

    const formSteps = serviceProviderForm.steps.map((step) => ({
      id: String(step.stepNumber),
      label: step.stepTitle,
      url: `/sp-invite/review/${providerType}/${step.stepKey}`,
    }));

    return formSteps.concat({
      id: String(formSteps.length + 1),
      label: "Summary",
      url: `/sp-invite/review/${providerType}/summary`,
    });
  }, [serviceProviderForm, providerType]);

  if (isCompleteSubmissionRoute) {
    return (
      <MantineProvider theme={{ fontFamily: "var(--font-text)" }}>
        <Outlet />
      </MantineProvider>
    );
  }

  if (isLoading) return <FullPageLoader />;
  if (error) return <div>Error loading data</div>;
  if (!serviceProviderForm) return <div>No Data found</div>;

  return (
    <MantineProvider
      theme={{
        fontFamily: "var(--font-text)",
      }}
    >
      <FormProvider {...methods}>
        <StepperFormLayout headerLabel="SP Onboarding Review" steps={reviewFormSteps} isReview>
          {isStepError ? (
            <div>Unable to load step data. Please try again.</div>
          ) : isStepLoading ? (
            <Loader />
          ) : (
            <div style={{ paddingTop: 10 }}>
              <Outlet />
            </div>
          )}
        </StepperFormLayout>
      </FormProvider>
    </MantineProvider>
  );
}

function FullPageLoader() {
  return (
    <Flex align="center" justify="center" style={{ minHeight: "70vh", width: "100%" }}>
      <Loader />
    </Flex>
  );
}
